import { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { canViewAgencies } from '../../utils/agencyAccess';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import ExecutiveKpiRow, { EXECUTIVE_KPI_DEFS } from '../../components/admin/ExecutiveKpiRow';
import ExecutiveCharts from '../../components/admin/ExecutiveCharts';
import MemberRevenueChart from '../../components/admin/MemberRevenueChart';
import ListLoadError from '../../components/admin/ListLoadError';
import { ExecutiveControlBar } from '../../components/admin/ExecutiveChartControls';
import {
  DEFAULT_EXECUTIVE_PERIOD,
  resolveOfferedPeriod,
  DEFAULT_EXECUTIVE_GRANULARITY,
  resolveOfferedGranularity,
  DEFAULT_EXECUTIVE_VIEW
} from '../../utils/executiveChartModel';
import { deriveSummaryTiles } from '../../utils/executiveSummaryTiles';
import api from '../../services/api';
import styles from './AdminDashboard.module.css';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell,
  BarChart,
  Bar,
  LabelList
} from 'recharts';

// User Growth is the ONE surface left on this page that is NOT driven by the executive Period
// control (card 5136): the legacy 7/14/30/90 pill row it used to follow was deleted, so its
// window is now fixed. It stays a named constant because three call sites read it — the
// `/admin/analytics/charts` query, `getChartInterval`, and the Area `dot` threshold.
const USER_GROWTH_TREND_DAYS = 30;

// Card 41953: a MAX Insight period cell must never substitute a page-level ALL-TIME scalar for a
// period datum the API did not publish — that paints a different population under an unchanged
// period label, and the reader has no way to tell. Measured live on 2026-08-31 (LEARNINGS:8154):
// when `/admin/analytics/super-dashboard` answered without `periodMetrics`, all four rows fell
// through to one page-level figure and the "Last 7 Days" row read 11.9x its true value.
// An unpublished datum is UNKNOWN — it is not zero, and it is not the all-time total.
const PERIOD_VALUE_UNAVAILABLE = '—';

// `0` and `'$0.00'` are real readings and must survive; only null/undefined/'' mean "not published".
const isPeriodValuePublished = (value) => value !== null && value !== undefined && value !== '';

const formatPeriodCount = (value) =>
  (isPeriodValuePublished(value) && Number.isFinite(Number(value))
    ? Number(value).toLocaleString()
    : PERIOD_VALUE_UNAVAILABLE);

// Card 41956: the MRR cells were the last in this table on a raw `||` chain — `today` fell through
// to a literal '$0.00', publishing a live membership base as worth nothing, and the other four
// reconstructed the cell from the page-level `revenueMetrics.mrr`. That borrow was never a WRONG
// number (`analyticsController` stamps `mrr: revenueMetrics.mrr`, so it is the same scalar) but it
// let the table speak when `periodMetrics` had said nothing, which is what hid the '$0.00' above it.
// This table now renders only what `periodMetrics` publishes. NOTE: MRR is identical on every row
// BY DESIGN — it is a STOCK, not a flow (commandCenterPeriods.js) — so do NOT 'vary' this column.
const formatPeriodAmount = (value) =>
  (isPeriodValuePublished(value) ? value : PERIOD_VALUE_UNAVAILABLE);

// Card 41954: a growth RATE is a reading about ONE period, so it cannot be filled in from another
// one. The badge borrowed exactly the way card 41953's value cells did: `?? clientsGrowth` painted
// the 30-day User Growth rate inside the "Last 7 Days" row, and `|| 0` printed a confident "0%" on
// the other four for a rate the API never published. Unpublished is UNKNOWN, not flat.
// A genuine 0 IS a reading and still renders "0%" — that is what `isPeriodValuePublished` buys.
const formatPeriodGrowth = (growth) =>
  (isPeriodValuePublished(growth) && Number.isFinite(Number(growth))
    ? `${Number(growth) > 0 ? '+' : ''}${growth}%`
    : PERIOD_VALUE_UNAVAILABLE);

// Icons
const UsersIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
    <circle cx="9" cy="7" r="4" />
    <path d="M23 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75" />
  </svg>
);

const RevenueIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <line x1="12" y1="1" x2="12" y2="23" />
    <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" />
  </svg>
);

// Card 4311: icon/tone per summary tile, keyed by the payload key so the tile list stays
// declared once in web/utils/executiveSummaryTiles.js and is not re-stated in JSX.
const SUMMARY_TILE_ICONS = Object.freeze({
  totalMembers: { icon: <UsersIcon />, style: { background: 'rgba(59,130,246,0.1)', color: '#3b82f6' } },
  newMembers: { icon: <UsersIcon />, style: { background: 'rgba(249,115,22,0.1)', color: '#f97316' } },
  grossRevenue: { icon: <RevenueIcon />, style: { background: 'rgba(34,197,94,0.1)', color: '#22c55e' } }
});

// Custom tooltip for charts
const CustomTooltip = ({ active, payload, label, valuePrefix = '', valueSuffix = '' }) => {
  if (!active || !payload || !payload.length) return null;

  return (
    <div style={{
      background: '#ffffff',
      border: '0.5px solid #f3e5d0',
      borderRadius: '8px',
      padding: '8px 12px',
      boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
    }}>
      <p style={{ margin: 0, fontSize: '0.75rem', color: '#737373' }}>{label}</p>
      <p style={{ margin: '4px 0 0', fontSize: '0.875rem', fontWeight: 600, color: '#2d2d2d' }}>
        {valuePrefix}{payload[0].value.toLocaleString()}{valueSuffix}
      </p>
    </div>
  );
};

// Card 53586: a leg that REJECTED, kept distinct from a leg that resolved with nothing.
// `{}` is what a permission-skipped leg resolves to and that is NOT a failure, so without
// this sentinel the loads below cannot tell "you may not see agencies" from "the agencies
// call broke" -- and both previously rendered as an empty agency list.
const LOAD_FAILED = { loadFailed: true };
const isLoadFailure = (response) => response === LOAD_FAILED;

const AdminDashboard = () => {
  const { hasPermission } = useAuth();
  const [chartData, setChartData] = useState(null);
  const [superMetrics, setSuperMetrics] = useState(null);
  const [agencyAnalytics, setAgencyAnalytics] = useState(null);
  const [agencies, setAgencies] = useState([]);
  const [selectedAgencyId, setSelectedAgencyId] = useState("");
  const [loading, setLoading] = useState(true);
  const [chartLoading, setChartLoading] = useState(false);
  const [memberCounts, setMemberCounts] = useState(null);
  const [memberRevenue, setMemberRevenue] = useState(null);
  const [executiveMetrics, setExecutiveMetrics] = useState(null);
  // Card 53586: a failed load previously left each of these at its mount value, so the page
  // rendered a missing section / an empty filter instead of saying the load failed.
  const [agenciesError, setAgenciesError] = useState(false);
  const [agencyAnalyticsError, setAgencyAnalyticsError] = useState(false);
  const [memberCountsError, setMemberCountsError] = useState(false);
  // Card 53610: #53586 left these two out, judging their surfaces "understated, not lying"
  // because the KPI tiles render an honest `—`. That is true of the executive band and FALSE
  // of `chartData`'s real consumer -- `clientsChartData` feeds the User Growth AreaChart, which
  // on a failed load draws both axes and NO series, i.e. a zero-growth window that never happened.
  const [chartError, setChartError] = useState(false);
  const [execError, setExecError] = useState(false);
  // `loadStats` lives inside the effect below, so retry re-runs the effect rather than
  // lifting the function out and changing how the page fetches.
  const [statsReloadKey, setStatsReloadKey] = useState(0);
  const period = USER_GROWTH_TREND_DAYS;
  // ONE control drives BOTH the executive KPI row and the executive charts (card 2255):
  // they read the same `executiveMetrics` object, so they cannot disagree about a period.
  const [execPeriod, setExecPeriod] = useState(DEFAULT_EXECUTIVE_PERIOD);
  // Grain is the GRAIN, period is the WINDOW — orthogonal, and both live here so the KPI row and
  // the charts read one fetch. View never reaches the API: both views draw the same response.
  const [execGranularity, setExecGranularity] = useState(DEFAULT_EXECUTIVE_GRANULARITY);
  const [execView, setExecView] = useState(DEFAULT_EXECUTIVE_VIEW);
  const [execLoading, setExecLoading] = useState(true);

  // Load all dashboard data (re-runs when agency changes)
  useEffect(() => {
    const loadStats = async () => {
      try {
        // Card 41953: `/admin/members/stats` and `/admin/orders/stats` were fetched here ONLY to
        // back the page-level scalars the MAX Insight period rows borrowed when the API published
        // no period datum. Those rows now render an explicit unavailable mark instead, so nothing
        // on this page reads either response — and `/admin/members/stats` in particular is the
        // UNFILTERED basis (8,566 against member-counts' 6,701 on 2026-09-16), i.e. exactly the
        // population this card exists to stop leaking in under another label. Dropping the two
        // legs also removes two full-table scans from every admin dashboard load.
        const [superDashboard, agencyAnalyticsData, agenciesResponse, memberCountsResponse, memberRevenueResponse] = await Promise.all([
          api.get(`/admin/analytics/super-dashboard${selectedAgencyId ? `?agencyId=${selectedAgencyId}` : ""}`),
          // Card 49621: /admin/agencies* is permission-gated server-side, and this
          // is the /admin INDEX route, so every landing by a role without
          // agencies.view spent two guaranteed 403s here.
          canViewAgencies(hasPermission)
            ? api.get('/admin/agencies/analytics').catch(() => LOAD_FAILED)
            : Promise.resolve({}),
          canViewAgencies(hasPermission)
            ? api.get('/admin/agencies').catch(() => LOAD_FAILED)
            : Promise.resolve({}),
          api.get(`/admin/analytics/member-counts${selectedAgencyId ? `?agencyId=${selectedAgencyId}` : ''}`).catch(() => LOAD_FAILED),
          api.get(`/admin/analytics/member-revenue${selectedAgencyId ? `?agencyId=${selectedAgencyId}` : ''}`).catch(() => ({ success: false })),
        ]);
        if (superDashboard.success) {
          setSuperMetrics(superDashboard.data);
        }
        // Agency analytics
        if (agencyAnalyticsData.topAgencies) {
          setAgencyAnalytics(agencyAnalyticsData);
        }
        setAgencyAnalyticsError(isLoadFailure(agencyAnalyticsData));
        // Agencies list for filter
        if (agenciesResponse.agencies) {
          setAgencies(agenciesResponse.agencies);
        }
        setAgenciesError(isLoadFailure(agenciesResponse));
        if (memberCountsResponse.success) {
          setMemberCounts(memberCountsResponse.data);
        }
        // Two failure shapes, both previously silent: a rejection, and a 200 that reports
        // `success: false`. Neither is a zero-member reading.
        setMemberCountsError(isLoadFailure(memberCountsResponse) || memberCountsResponse.success === false);
        if (memberRevenueResponse.success) {
          setMemberRevenue(memberRevenueResponse.data);
        }
      } catch (error) {
        // The super-dashboard leg carries no inline catch, so its rejection lands here and
        // skips every setter above -- including the three flags. Raise them all, or a failure
        // of that one leg silently restores the exact defect this card fixes.
        console.error('Failed to load stats:', error);
        setAgenciesError(true);
        setAgencyAnalyticsError(true);
        setMemberCountsError(true);
      } finally {
        setLoading(false);
      }
    };

    loadStats();
  }, [selectedAgencyId, hasPermission, statsReloadKey]);

  // Load chart data
  const loadChartData = useCallback(async () => {
    setChartLoading(true);
    try {
      const response = await api.get(`/admin/analytics/charts?period=${period}${selectedAgencyId ? `&agencyId=${selectedAgencyId}` : ""}`);
      if (response.success) {
        setChartData(response.data);
      }
      // Two failure shapes, both previously silent: a rejection, and a 200 that reports
      // `success: false`. Neither is a zero-growth window. Mirrors the member-counts leg above.
      setChartError(response.success === false);
    } catch (error) {
      console.error('Failed to load chart data:', error);
      setChartError(true);
    } finally {
      setChartLoading(false);
    }
  }, [period, selectedAgencyId]);

  useEffect(() => {
    loadChartData();
  }, [loadChartData]);

  // Executive KPI row + executive charts, from ONE request driven by ONE control.
  // `resolveOfferedPeriod` guarantees the key sent is one the endpoint accepts — an
  // unknown period answers 400, which would blank both surfaces with no console error.
  const loadExecutive = useCallback(async () => {
    setExecLoading(true);
    try {
      const key = resolveOfferedPeriod(execPeriod);
      const grain = resolveOfferedGranularity(execGranularity);
      const response = await api.get(`/admin/analytics/executive?period=${key}&granularity=${grain}`);
      if (response.success) {
        setExecutiveMetrics(response.data);
      }
      setExecError(response.success === false);
    } catch (error) {
      console.error('Failed to load executive metrics:', error);
      setExecError(true);
    } finally {
      setExecLoading(false);
    }
  }, [execPeriod, execGranularity]);

  useEffect(() => {
    loadExecutive();
  }, [loadExecutive]);

  if (loading) {
    return <LoadingSpinner fullScreen />;
  }

  // Card 41953: `totalClients` / `totalRevenue` / `activeSubscriptions` were declared here ONLY to
  // back-fill the MAX Insight period rows, and they are all-time figures on a THIRD basis again —
  // `chartData.clients.total` excludes flagged members, `stats.members.total` does not, so the
  // value silently changed population depending on which leg of the fetch had answered. The period
  // rows now render an explicit unavailable mark instead, which leaves these with no reader.

  // Chart data
  const clientsChartData = chartData?.clients?.trend || [];

  // Card 41954: `clientsGrowth` / `revenueGrowth` / `subscriptionsGrowth` were declared here ONLY
  // to back-fill the "Last 7 Days" growth badges, and they are the 30-DAY User Growth chart's
  // rates (USER_GROWTH_TREND_DAYS) — a different window than the row that borrowed them. The
  // badges render an explicit unavailable mark instead, which leaves these with no reader.

  const getChartInterval = (p) => {
    if (p <= 7) return 0;
    if (p <= 14) return 1;
    if (p <= 30) return 4;
    return 13;
  };

  // Fed the RAW datum now, so it must answer the unpublished case itself rather than relying on
  // `undefined > 0` and `undefined < 0` both being false — an implicit neutral that would silently
  // pick a colour for a rate nobody published.
  const getGrowthClass = (growth) => {
    if (!isPeriodValuePublished(growth) || !Number.isFinite(Number(growth))) return styles.neutral;
    if (Number(growth) > 0) return styles.positive;
    if (Number(growth) < 0) return styles.negative;
    return styles.neutral;
  };

  // Card 4311: the summary band reuses the executive payload and EXECUTIVE_KPI_DEFS's OWN
  // per-key formatter, so these three tiles cannot drift from the six KPI cards above them.
  const showSummaryTiles = hasPermission('dashboard_revenue.view');
  const showUserGrowth = hasPermission('dashboard_users.view');
  // Card 53628: `loadExecutive` sets the payload ONLY on success and never resets it, and
  // `execPeriod` is in its dependency list — so a FAILED period switch leaves it holding the
  // window the user just navigated away from. Card 53610 gated the band above on `execError`,
  // but that gate never reached these tiles, and they are the worse surface: a first-load
  // failure has nothing to render and honestly shows `—`, while a failed SWITCH shows three
  // confident figures for the wrong window. Reusing the flag the band already trusts keeps this
  // pure; the named error sits directly above and is what explains the dashes.
  const summaryTiles = deriveSummaryTiles(EXECUTIVE_KPI_DEFS, execError ? null : executiveMetrics);

  return (
    <div className={styles.page}>
      <div className={styles.header}>
        <h1>MAX Insight</h1>
        <p className={styles.subtitle}>Deep analytics and insights across your platform</p>
        <select
          className={styles.agencyFilter}
          value={selectedAgencyId}
          onChange={(e) => setSelectedAgencyId(e.target.value)}
          disabled={agenciesError}
        >
          <option value="">All Agencies</option>
          {agencies.map(agency => (
            <option key={agency.id} value={agency.id}>{agency.name}</option>
          ))}
        </select>
        {agenciesError && (
          <p className={styles.filterError} role="alert">
            Agencies could not be loaded, so this filter is unavailable. It is not showing an
            empty list because no agencies exist.{' '}
            <button
              type="button"
              className={styles.filterRetry}
              onClick={() => setStatsReloadKey(key => key + 1)}
            >
              Try again
            </button>
          </p>
        )}
      </div>

      {/* Executive overview (cards 2254 + 2255) — ONE period control, ONE request,
          feeding both the KPI row and the three charts from the same object. */}
      {hasPermission('dashboard_revenue.view') && (
        <>
          <ExecutiveControlBar
            period={execPeriod}
            onPeriodChange={setExecPeriod}
            granularity={execGranularity}
            onGranularityChange={setExecGranularity}
            view={execView}
            onViewChange={setExecView}
            disabled={execLoading}
          />
          {execError ? (
            <ListLoadError
              message="Could not load executive metrics"
              onRetry={loadExecutive}
            />
          ) : (
            <>
              <ExecutiveKpiRow metrics={executiveMetrics} loading={execLoading} period={execPeriod} />
              <ExecutiveCharts metrics={executiveMetrics} loading={execLoading} view={execView} />
            </>
          )}
        </>
      )}

      {/* Member Revenue (card 4531 — REQ-01/REQ-06). The highest slot on the page that is
          OUTSIDE the executive product/expense band: immediately after that band closes and
          before the summary band. Mounting it INSIDE the executive band would interleave member
          money with product money, which REQ-06 forbids. The payload comes from the same
          Promise.all above, so this is not a second waterfall.
          `loading` is the PAGE flag, not a negation of the payload: the page early-returns
          while it is true, so by the time this renders the fetch has SETTLED and a null payload
          means no-data. Deriving the flag from the payload instead rendered the spinner state
          forever on a failed fetch — exactly the invisible-failure shape card 4530 built the
          explicit empty state to avoid. */}
      {hasPermission('dashboard_revenue.view') && (
        <div className={styles.chartsGrid}>
          <MemberRevenueChart payload={memberRevenue} loading={loading} />
        </div>
      )}

      {/* Summary band (card 4311) — three summary tiles LEFT, the growth chart RIGHT.
          The brief asks for a FULL-WIDTH timeline, so nothing may sit beside the chart:
          the left/right therefore describes THIS band, not the chart row. Collapses to a
          single track at <=1024px and whenever the viewer holds only one of the two
          permissions, so the band can never leave a dead column. */}
      {(showSummaryTiles || showUserGrowth) && (
        <div
          className={showSummaryTiles && showUserGrowth
            ? styles.summaryBand
            : `${styles.summaryBand} ${styles.summaryBandSingle}`}
          data-testid="executive-summary-band"
        >
          {showSummaryTiles && (
            <div className={styles.summaryRow}>
              {summaryTiles.map((tile) => (
                <div
                  key={tile.key}
                  className={styles.summaryCard}
                  data-testid={`summary-tile-${tile.key}`}
                  data-value={tile.value}
                >
                  <div className={styles.summaryIcon} style={SUMMARY_TILE_ICONS[tile.key].style}>
                    {SUMMARY_TILE_ICONS[tile.key].icon}
                  </div>
                  <div className={styles.summaryValue}>{tile.value}</div>
                  <div className={styles.summaryLabel}>{tile.label}</div>
                </div>
              ))}
            </div>
          )}

          {showUserGrowth && (
            <div className={styles.chartCard} data-testid="user-growth-card">
            <div className={styles.chartHeader}>
              <h3 className={styles.chartTitle}>User Growth · Last {USER_GROWTH_TREND_DAYS} Days</h3>
            </div>
            <div className={styles.chartContainer}>
              {chartLoading ? (
                <div className={styles.chartLoading}>Loading...</div>
              ) : chartError ? (
                <ListLoadError
                  message="Could not load user growth"
                  onRetry={loadChartData}
                />
              ) : (
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={clientsChartData} margin={{ top: 5, right: 5, left: -20, bottom: 0 }}>
                    <defs>
                      <linearGradient id="colorClients" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.2}/>
                        <stop offset="95%" stopColor="#3b82f6" stopOpacity={0}/>
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" vertical={false} />
                    <XAxis dataKey="label" tick={{ fontSize: 10, fill: '#9ca3af' }} tickLine={false} axisLine={{ stroke: '#f0f0f0' }} interval={getChartInterval(period)} />
                    <YAxis tick={{ fontSize: 10, fill: '#9ca3af' }} tickLine={false} axisLine={false} allowDecimals={false} />
                    <Tooltip content={<CustomTooltip />} />
                    <Area type="linear" dataKey="cumulative" stroke="#3b82f6" strokeWidth={2} fillOpacity={0.12} fill="url(#colorClients)" dot={period <= 14 ? { r: 2.5, strokeWidth: 1.5 } : false} activeDot={{ r: 4 }} />
                  </AreaChart>
                </ResponsiveContainer>
              )}
            </div>
            </div>
          )}
        </div>
      )}

      {/* Card 53586: on a failed load `memberCounts` stays null, so the block below rendered
          NOTHING and the whole section vanished -- indistinguishable from "this deployment has
          no member counts". This sibling is mutually exclusive with it (null vs non-null) and
          states the failure instead, so the block below is unchanged. */}
      {hasPermission('dashboard_users.view') && memberCountsError && (
        <div className={styles.chartCard} style={{ marginBottom: '24px' }}>
          <div className={styles.chartHeader}>
            <h3 className={styles.chartTitle}>Member Counts</h3>
          </div>
          <ListLoadError
            message="Could not load member counts"
            onRetry={() => setStatsReloadKey(key => key + 1)}
          />
        </div>
      )}

      {/* Member Counts Bar Chart */}
      {hasPermission('dashboard_users.view') && memberCounts && (
        <div className={styles.chartCard} style={{ marginBottom: '24px' }}>
          <div className={styles.chartHeader}>
            <h3 className={styles.chartTitle}>Member Counts</h3>
          </div>
          {/* Card 41953: these three bars are bare Active/Inactive/Total, but they are NOT the
              same population as /admin/members/stats, which this page also reads. `member-counts`
              applies the reporting exclusion (api/utils/reportingExclusion.js), so it is short by
              every flagged test/demo member — measured 6,701 here against 8,566 there in the same
              minute on 2026-09-16, a spread of 1,865. A count with no population attached is not
              gradeable by the operator reading it, so the panel names its basis. */}
          <p className={styles.chartBasisNote}>
            Excludes members flagged as test or demo accounts.
          </p>
          <div className={styles.chartContainer} style={{ height: '280px' }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart
                data={[
                  { name: 'Active', value: memberCounts.active, fill: '#22c55e' },
                  { name: 'Inactive', value: memberCounts.inactive, fill: '#ef4444' },
                  { name: 'Total', value: memberCounts.total, fill: '#3b82f6' }
                ]}
                margin={{ top: 20, right: 30, left: 0, bottom: 5 }}
              >
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" vertical={false} />
                <XAxis dataKey="name" tick={{ fontSize: 12, fill: '#737373', fontWeight: 600 }} tickLine={false} axisLine={{ stroke: '#f0f0f0' }} />
                <YAxis tick={{ fontSize: 10, fill: '#9ca3af' }} tickLine={false} axisLine={false} allowDecimals={false} />
                <Tooltip content={<CustomTooltip />} />
                <Bar dataKey="value" radius={[6, 6, 0, 0]} barSize={60}>
                  <LabelList dataKey="value" position="top" style={{ fontSize: '13px', fontWeight: 700, fill: '#2d2d2d' }} />
                  {[
                    { color: '#22c55e' },
                    { color: '#ef4444' },
                    { color: '#3b82f6' }
                  ].map((entry, index) => (
                    <Cell key={`member-cell-${index}`} fill={entry.color} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
          <div style={{ display: 'flex', justifyContent: 'center', gap: '24px', padding: '8px 0 4px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <span style={{ width: '10px', height: '10px', borderRadius: '2px', backgroundColor: '#22c55e', display: 'inline-block' }} />
              <span style={{ fontSize: '0.75rem', color: '#737373' }}>Active</span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <span style={{ width: '10px', height: '10px', borderRadius: '2px', backgroundColor: '#ef4444', display: 'inline-block' }} />
              <span style={{ fontSize: '0.75rem', color: '#737373' }}>Inactive</span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <span style={{ width: '10px', height: '10px', borderRadius: '2px', backgroundColor: '#3b82f6', display: 'inline-block' }} />
              <span style={{ fontSize: '0.75rem', color: '#737373' }}>Total</span>
            </div>
          </div>
        </div>
      )}

      {/* Command Center - Key Metrics with Time Periods */}
      {hasPermission('dashboard_users.view') && <div className={styles.keyMetricsCard}>
        <div className={styles.spreadsheetHeader} style={{ borderLeftColor: '#f97316' }}>
          <div className={styles.spreadsheetIcon} style={{ backgroundColor: 'rgba(249, 115, 22, 0.1)', color: '#f97316' }}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M12 20V10" /><path d="M18 20V4" /><path d="M6 20v-4" />
            </svg>
          </div>
          <span className={styles.spreadsheetTitle}>MAX Insight Command Center</span>
        </div>
        <div className={styles.keyMetricsTableHeader}>
          <span className={styles.keyMetricsColPeriod}>Period</span>
          <span className={styles.keyMetricsCol}>Users</span>
          <span className={styles.keyMetricsCol}>Revenue</span>
          <span className={styles.keyMetricsCol}>Subscriptions</span>
          <span className={styles.keyMetricsCol}>MRR</span>
        </div>
        <div className={styles.keyMetricsTableBody}>
          <div className={styles.keyMetricsTableRow}>
            <span className={styles.keyMetricsColPeriod}>Today</span>
            <span className={styles.keyMetricsCol}>
              {formatPeriodCount(superMetrics?.periodMetrics?.today?.users)}
              <span className={`${styles.inlineGrowth} ${getGrowthClass(superMetrics?.periodMetrics?.today?.usersGrowth)}`}>
                {formatPeriodGrowth(superMetrics?.periodMetrics?.today?.usersGrowth)}
              </span>
            </span>
            <span className={styles.keyMetricsCol}>
              {formatPeriodAmount(superMetrics?.periodMetrics?.today?.revenue)}
              <span className={`${styles.inlineGrowth} ${getGrowthClass(superMetrics?.periodMetrics?.today?.revenueGrowth)}`}>
                {formatPeriodGrowth(superMetrics?.periodMetrics?.today?.revenueGrowth)}
              </span>
            </span>
            <span className={styles.keyMetricsCol}>
              {formatPeriodCount(superMetrics?.periodMetrics?.today?.subscriptions)}
              <span className={`${styles.inlineGrowth} ${getGrowthClass(superMetrics?.periodMetrics?.today?.subscriptionsGrowth)}`}>
                {formatPeriodGrowth(superMetrics?.periodMetrics?.today?.subscriptionsGrowth)}
              </span>
            </span>
            <span className={styles.keyMetricsCol}>{formatPeriodAmount(superMetrics?.periodMetrics?.today?.mrr)}</span>
          </div>
          <div className={styles.keyMetricsTableRow}>
            <span className={styles.keyMetricsColPeriod}>Last 7 Days</span>
            <span className={styles.keyMetricsCol}>
              {formatPeriodCount(superMetrics?.periodMetrics?.week?.users)}
              <span className={`${styles.inlineGrowth} ${getGrowthClass(superMetrics?.periodMetrics?.week?.usersGrowth)}`}>
                {formatPeriodGrowth(superMetrics?.periodMetrics?.week?.usersGrowth)}
              </span>
            </span>
            <span className={styles.keyMetricsCol}>
              {formatPeriodAmount(superMetrics?.periodMetrics?.week?.revenue)}
              <span className={`${styles.inlineGrowth} ${getGrowthClass(superMetrics?.periodMetrics?.week?.revenueGrowth)}`}>
                {formatPeriodGrowth(superMetrics?.periodMetrics?.week?.revenueGrowth)}
              </span>
            </span>
            <span className={styles.keyMetricsCol}>
              {formatPeriodCount(superMetrics?.periodMetrics?.week?.subscriptions)}
              <span className={`${styles.inlineGrowth} ${getGrowthClass(superMetrics?.periodMetrics?.week?.subscriptionsGrowth)}`}>
                {formatPeriodGrowth(superMetrics?.periodMetrics?.week?.subscriptionsGrowth)}
              </span>
            </span>
            <span className={styles.keyMetricsCol}>{formatPeriodAmount(superMetrics?.periodMetrics?.week?.mrr)}</span>
          </div>
          <div className={styles.keyMetricsTableRow}>
            <span className={styles.keyMetricsColPeriod}>Last 30 Days</span>
            <span className={styles.keyMetricsCol}>
              {formatPeriodCount(superMetrics?.periodMetrics?.month?.users)}
              <span className={`${styles.inlineGrowth} ${getGrowthClass(superMetrics?.periodMetrics?.month?.usersGrowth)}`}>
                {formatPeriodGrowth(superMetrics?.periodMetrics?.month?.usersGrowth)}
              </span>
            </span>
            <span className={styles.keyMetricsCol}>
              {formatPeriodAmount(superMetrics?.periodMetrics?.month?.revenue)}
              <span className={`${styles.inlineGrowth} ${getGrowthClass(superMetrics?.periodMetrics?.month?.revenueGrowth)}`}>
                {formatPeriodGrowth(superMetrics?.periodMetrics?.month?.revenueGrowth)}
              </span>
            </span>
            <span className={styles.keyMetricsCol}>
              {formatPeriodCount(superMetrics?.periodMetrics?.month?.subscriptions)}
              <span className={`${styles.inlineGrowth} ${getGrowthClass(superMetrics?.periodMetrics?.month?.subscriptionsGrowth)}`}>
                {formatPeriodGrowth(superMetrics?.periodMetrics?.month?.subscriptionsGrowth)}
              </span>
            </span>
            <span className={styles.keyMetricsCol}>{formatPeriodAmount(superMetrics?.periodMetrics?.month?.mrr)}</span>
          </div>
          <div className={styles.keyMetricsTableRow}>
            <span className={styles.keyMetricsColPeriod}>Last 90 Days</span>
            <span className={styles.keyMetricsCol}>
              {formatPeriodCount(superMetrics?.periodMetrics?.quarter?.users)}
              <span className={`${styles.inlineGrowth} ${getGrowthClass(superMetrics?.periodMetrics?.quarter?.usersGrowth)}`}>
                {formatPeriodGrowth(superMetrics?.periodMetrics?.quarter?.usersGrowth)}
              </span>
            </span>
            <span className={styles.keyMetricsCol}>
              {formatPeriodAmount(superMetrics?.periodMetrics?.quarter?.revenue)}
              <span className={`${styles.inlineGrowth} ${getGrowthClass(superMetrics?.periodMetrics?.quarter?.revenueGrowth)}`}>
                {formatPeriodGrowth(superMetrics?.periodMetrics?.quarter?.revenueGrowth)}
              </span>
            </span>
            <span className={styles.keyMetricsCol}>
              {formatPeriodCount(superMetrics?.periodMetrics?.quarter?.subscriptions)}
              <span className={`${styles.inlineGrowth} ${getGrowthClass(superMetrics?.periodMetrics?.quarter?.subscriptionsGrowth)}`}>
                {formatPeriodGrowth(superMetrics?.periodMetrics?.quarter?.subscriptionsGrowth)}
              </span>
            </span>
            <span className={styles.keyMetricsCol}>{formatPeriodAmount(superMetrics?.periodMetrics?.quarter?.mrr)}</span>
          </div>
          <div className={styles.keyMetricsTableRow}>
            <span className={styles.keyMetricsColPeriod}>Last 1 Year</span>
            <span className={styles.keyMetricsCol}>
              {formatPeriodCount(superMetrics?.periodMetrics?.year?.users)}
              <span className={`${styles.inlineGrowth} ${getGrowthClass(superMetrics?.periodMetrics?.year?.usersGrowth)}`}>
                {formatPeriodGrowth(superMetrics?.periodMetrics?.year?.usersGrowth)}
              </span>
            </span>
            <span className={styles.keyMetricsCol}>
              {formatPeriodAmount(superMetrics?.periodMetrics?.year?.revenue)}
              <span className={`${styles.inlineGrowth} ${getGrowthClass(superMetrics?.periodMetrics?.year?.revenueGrowth)}`}>
                {formatPeriodGrowth(superMetrics?.periodMetrics?.year?.revenueGrowth)}
              </span>
            </span>
            <span className={styles.keyMetricsCol}>
              {formatPeriodCount(superMetrics?.periodMetrics?.year?.subscriptions)}
              <span className={`${styles.inlineGrowth} ${getGrowthClass(superMetrics?.periodMetrics?.year?.subscriptionsGrowth)}`}>
                {formatPeriodGrowth(superMetrics?.periodMetrics?.year?.subscriptionsGrowth)}
              </span>
            </span>
            <span className={styles.keyMetricsCol}>{formatPeriodAmount(superMetrics?.periodMetrics?.year?.mrr)}</span>
          </div>
        </div>
      </div>}

      {/* Best Selling Products */}
      {hasPermission('dashboard_revenue.view') && <div className={styles.metricsSection}>
        <h2 className={styles.sectionTitle}>Best Selling Products</h2>
        <div className={styles.productTableCard}>
          <div className={styles.sourceTableHeader}>
            <span className={styles.productColName}>Product</span>
            <span className={styles.sourceCol}>Units Sold</span>
            <span className={styles.sourceCol}>Revenue</span>
            <span className={styles.sourceCol}>Orders</span>
            <span className={styles.sourceCol}>AOV</span>
            <span className={styles.sourceCol}>Return Rate</span>
            <span className={styles.sourceCol}>Rating</span>
          </div>
          <div className={styles.sourceTableBody}>
            {(superMetrics?.productMetrics?.bestSelling || [
              { name: 'Compounded Semaglutide', key: 'compounded-semaglutide' },
              { name: 'Wegovy', key: 'wegovy' },
              { name: 'Semaglutide FastSL', key: 'semaglutide-fastsl' },
              { name: 'Sildenafil', key: 'sildenafil' },
              { name: 'Sildenafil Combo', key: 'sildenafil-combo' },
              { name: 'Tadalafil', key: 'tadalafil' },
              { name: 'Finasteride Combo', key: 'finasteride-combo' },
              { name: 'Minoxidil', key: 'minoxidil' },
              { name: 'Minoxidil Topical', key: 'minoxidil-topical' },
              { name: 'Tretinoin Combo', key: 'tretinoin-combo' },
              { name: 'Enclomiphene', key: 'enclomiphene' },
              { name: 'Metformin XR', key: 'metformin-xr' },
              { name: 'Oxytocin Nasal', key: 'oxytocin-nasal' },
              { name: 'Sildenafil Topical', key: 'sildenafil-topical' }
            ]).map((product, index) => (
              // `product.key` is a LOSSY slug of `name` (`[^a-z0-9]+` -> `-`), so distinct
              // products can share one key — "NAD+" and "NAD+ " both slug to `nad-`. `name`
              // is the upstream Map key, so its uniqueness is structural, not incidental.
              <div key={product.name || `product-${index}`} className={styles.sourceTableRow}>
                <span className={styles.productColName}>
                  <span className={styles.productRank}>#{index + 1}</span>
                  {product.name}
                </span>
                <span className={styles.sourceCol}>{product.unitsSold?.toLocaleString() || 0}</span>
                <span className={styles.sourceCol}>${product.revenue?.toLocaleString() || '0'}</span>
                <span className={styles.sourceCol}>{product.orders?.toLocaleString() || 0}</span>
                <span className={styles.sourceCol}>${product.aov || '0'}</span>
                <span className={`${styles.sourceCol} ${styles.churnCol}`}>{product.returnRate || '0%'}</span>
                <span className={`${styles.sourceCol} ${styles.ratingCol}`}>{product.rating || '0.0'} ★</span>
              </div>
            ))}
          </div>
        </div>
      </div>}

      {/* Subscription Tier Distribution */}
      {hasPermission('dashboard_subscriptions.view') && superMetrics?.subscriptionMetrics?.tierDistribution?.length > 0 && (
        <div className={styles.tierCard} style={{ marginBottom: '24px' }}>
          <h3 className={styles.tierTitle}>Subscription Tiers</h3>
          <div className={styles.tierList}>
            {superMetrics.subscriptionMetrics.tierDistribution.map((tier, index) => (
              <div key={index} className={styles.tierItem}>
                <div className={styles.tierInfo}>
                  <span className={styles.tierName}>{tier.name}</span>
                  <span className={styles.tierCount}>{tier.count} users</span>
                </div>
                <div className={styles.tierBarWrapper}>
                  <div
                    className={styles.tierBar}
                    style={{ width: `${tier.percentage}%`, backgroundColor: ['#3b82f6', '#8b5cf6', '#22c55e', '#f59e0b'][index % 4] }}
                  />
                </div>
                <span className={styles.tierPercent}>{tier.percentage}%</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Card 53586: same shape as Member Counts above. A failed `/admin/agencies/analytics`
          left `agencyAnalytics` null and removed all three panels, so the `No agencies with
          clients` / `No subscription data` empty states INSIDE were never even reached. Those
          stay exactly as they were and still speak for a genuine zero. */}
      {hasPermission('dashboard_csv.view') && agencyAnalyticsError && (
        <div className={styles.metricsSection}>
          <h2 className={styles.sectionTitle}>Agency Analytics</h2>
          <ListLoadError
            message="Could not load agency analytics"
            onRetry={() => setStatsReloadKey(key => key + 1)}
          />
        </div>
      )}

      {/* Agency Analytics Section */}
      {hasPermission('dashboard_csv.view') && agencyAnalytics && (
        <div className={styles.metricsSection}>
          <h2 className={styles.sectionTitle}>Agency Analytics</h2>
          <div className={styles.agencyAnalyticsGrid}>
            {/* Top Agencies */}
            <div className={styles.sourceTableCard}>
              <h3 className={styles.tableTitle}>Top Agencies by Client Count</h3>
              <div className={styles.simpleTable}>
                {agencyAnalytics.topAgencies.slice(0, 5).map((agency, index) => (
                  <div key={agency.id} className={styles.simpleTableRow}>
                    <span className={styles.simpleTableRank}>#{index + 1}</span>
                    <Link to={`/admin/agencies/${agency.id}`} className={styles.simpleTableName}>
                      {agency.name}
                    </Link>
                    <span className={styles.simpleTableValue}>{agency.clientCount} clients</span>
                  </div>
                ))}
                {agencyAnalytics.topAgencies.length === 0 && (
                  <div className={styles.emptyState}>No agencies with clients</div>
                )}
              </div>
            </div>

            {/* Agency Growth Chart */}
            <div className={styles.chartCard}>
              <h3 className={styles.chartTitle}>Agency Sign-ups (Last 6 Months)</h3>
              <ResponsiveContainer width="100%" height={200}>
                <AreaChart data={agencyAnalytics.agencyGrowth}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f3e5d0" />
                  <XAxis dataKey="month" stroke="#737373" fontSize={12} />
                  <YAxis stroke="#737373" fontSize={12} />
                  <Tooltip content={<CustomTooltip />} />
                  <Area
                    type="linear"
                    dataKey="signups"
                    stroke="#f97316"
                    fill="#fff7ed"
                    strokeWidth={2}
                  />
                </AreaChart>
              </ResponsiveContainer>
            </div>

            {/* Subscription Distribution */}
            <div className={styles.sourceTableCard}>
              <h3 className={styles.tableTitle}>Subscriptions by Agency</h3>
              <div className={styles.simpleTable}>
                {agencyAnalytics.subscriptionDistribution.slice(0, 5).map((agency) => (
                  <div key={agency.name} className={styles.simpleTableRow}>
                    <span className={styles.simpleTableName}>{agency.name}</span>
                    <div className={styles.subscriptionBadges}>
                      <span className={styles.badgeActive}>{agency.active} active</span>
                      {agency.trial > 0 && <span className={styles.badgeTrial}>{agency.trial} trial</span>}
                      {agency.cancelled > 0 && <span className={styles.badgeCancelled}>{agency.cancelled} cancelled</span>}
                    </div>
                  </div>
                ))}
                {agencyAnalytics.subscriptionDistribution.length === 0 && (
                  <div className={styles.emptyState}>No subscription data</div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default AdminDashboard;
